// Whether a request id on an incoming stream is one this tab asked for. The
// `stale` action skips its own notice by this, so getting it wrong shows an
// author the reload prompt for the write they just made.
//
// Two libraries make those requests, and they have to be answered differently.
//
// Turbo mints its `X-Turbo-Request-Id` inside `fetchWithTurboHeaders`, at the
// moment of `window.fetch` and onto a *copy* of the headers
// (`new Headers(options.headers)`). It is therefore absent from
// `event.detail.fetchOptions.headers` when `turbo:before-fetch-request` fires,
// and mirroring it from that event records nothing at all. What Turbo does do
// is add the id to the `recentRequests` set it hands its `Session` — the same
// set its own `refresh` stream action asks before skipping a tab's own refresh.
// Asking it here is asking the question Turbo already answers for itself.
//
// `@rails/request.js` sends no request id: its `get headers` builds
// `X-Requested-With`, `Content-Type`, `Accept` and the CSRF token and nothing
// else. The Table of Contents drag and the board's event drag post through it,
// so those call sites mint an id of ours and send it as the header explicitly —
// which is what `requestIdHeaders` is for, and why we keep a set of our own.

const MAX_TRACKED = 32

let ids = []

export function resetRequestIds () {
  ids = []
}

export function rememberRequestId (requestId) {
  if (!requestId) return requestId

  if (!ids.includes(requestId)) {
    ids.push(requestId)
    if (ids.length > MAX_TRACKED) ids.shift()
  }

  return requestId
}

export function isOwnRequestId (requestId) {
  if (!requestId) return false

  return ids.includes(requestId) || turboSentRequestId(requestId)
}

// Read off `window.Turbo` rather than imported, and guarded: `session` is
// exported but `recentRequests` is not part of Turbo's documented surface, so a
// version that moves it must cost a missed filter — an author shown one notice
// too many — and not a stream action that throws.
function turboSentRequestId (requestId) {
  try {
    return Boolean(globalThis.Turbo?.session?.recentRequests?.has(requestId))
  } catch {
    return false
  }
}

export function mintRequestId () {
  return rememberRequestId(crypto.randomUUID())
}

export function requestIdHeaders (headers = {}) {
  return { ...headers, 'X-Turbo-Request-Id': mintRequestId() }
}
